import { useCallback, useState } from 'react';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { DixaMessengerSubflow } from 'components/settings/components/sections/dixaMessenger/views/flow/hooks';
import { Relation } from 'fable/flowEditor/components/flowManager/canvas/types/relation';

import { SubflowMessengerAction } from '../flow.types';

type FlowValidationFunction = (allActions: SubflowMessengerAction[]) =>
  | { status: 'success' }
  | {
      status: 'failure';
      failingNodeIds: string[];
    };

const isIsolatedNode: FlowValidationFunction = (actions) => {
  // Do not allow isolated nodes with no parent or children also do not allow any unconnected ports
  const failingNodeIds = actions.reduce((failingNodeIds, action) => {
    const isIsolated =
      (action as Relation).connections?.length === 0 &&
      !action.parentId &&
      action.nodeType !== FlowNodeTypes.INIT;

    if (isIsolated) {
      return [...failingNodeIds, action.id];
    }

    return failingNodeIds;
  }, [] as string[]);

  const hasIsolatedNodes = failingNodeIds.length > 0;

  if (hasIsolatedNodes) {
    return { status: 'failure', failingNodeIds };
  }

  return { status: 'success' };
};

const hasUnconnectedPorts: FlowValidationFunction = (actions) => {
  const failingNodeIds = actions.reduce((failingNodeIds, action) => {
    const foundConnectButton =
      (action.nodeType as unknown as FlowNodeTypes) === FlowNodeTypes.ACTION;

    if (foundConnectButton) {
      // return the id of the parent since an action node indicates an unconnected port on the parent node.
      return [...failingNodeIds, action.parentId ?? action.id];
    }

    return failingNodeIds;
  }, [] as string[]);

  if (failingNodeIds.length > 0) {
    return { status: 'failure', failingNodeIds };
  }

  return { status: 'success' };
};

const nodeValidators: FlowValidationFunction[] = [
  isIsolatedNode,
  hasUnconnectedPorts,
  // Add more validation functions here
];

const runValidators = (actions: DixaMessengerSubflow['actions']): string[] => {
  const results = nodeValidators
    .map((validator) => validator(actions))
    .flatMap((result) =>
      result.status === 'success' ? [] : result.failingNodeIds,
    );

  const uniqueFailingNodeIds = [...new Set(results)];

  return uniqueFailingNodeIds;
};

type ValidationResult = {
  invalidNodeIds: string[];
  triggerValidation: () => string[];
};

export function useSubflowValidation(
  actions: DixaMessengerSubflow['actions'],
): ValidationResult {
  const [invalidNodeIds, setInvalidNodeIds] = useState<string[]>([]);

  const triggerValidation = useCallback(() => {
    const invalidNodeIds = runValidators(actions);
    setInvalidNodeIds(invalidNodeIds);
    return invalidNodeIds;
  }, [actions]);

  return { invalidNodeIds, triggerValidation };
}

export const genericValidationError =
  'Please ensure all nodes are connected or add a "Back to main flow" to have connectors outside the sub-flow.';
